import { useEffect, useRef, useState } from "react";
import type { DayKey } from "@/lib/days";

/**
 * The open/collapsed state of a day widget card: open until the day reads
 * Ferdiglogget (CONTEXT.md), foldable by hand, and re-armed whenever that
 * automatic answer changes.
 *
 * A fold by hand holds — but only until the day on screen changes, or the day
 * *becomes* finished. Neither exception is optional. Opening a card to keep
 * logging must not cost the collapse being logged towards, and `Day.tsx` keys
 * these cards on the widget id rather than on the date, so navigating to
 * another day re-renders them rather than remounting: an override naming
 * neither would follow you across the whole week.
 *
 * `ready` means "this answer is worth acting on yet", and covers two cases.
 * Body measurements load asynchronously and read as not-logged until they
 * land, which would otherwise pin an already-logged day open. Sleep-log and
 * workday-log defer their answer while a field has focus, so a focus
 * round-trip flickers it false and back — not a completion change, and folding
 * a card away between two corrections is the cost of mistaking it for one.
 *
 * Returns `open` rather than `collapsed`, because whether a card offers the
 * chevron at all is each kind's own business — some only once complete.
 */
export function useAutoCollapse(
  dayKey: DayKey,
  loggingCompleted: boolean,
  ready = true,
): { open: boolean; toggle: () => void } {
  const auto = useRef({ day: dayKey, completed: loggingCompleted, ready });
  const [open, setOpen] = useState(() => !(ready && loggingCompleted));

  useEffect(() => {
    if (!ready) return;
    const previous = auto.current;
    if (
      previous.ready &&
      previous.day === dayKey &&
      previous.completed === loggingCompleted
    ) {
      return;
    }
    auto.current = { day: dayKey, completed: loggingCompleted, ready };
    setOpen(!loggingCompleted);
  }, [dayKey, loggingCompleted, ready]);

  return {
    open,
    toggle: () => setOpen((o) => !o),
  };
}
